/**
 * Admin accounts — super admins only.
 *
 * The Admin Portal is Google sign-in only: a Google account can sign in only if an Admin
 * Portal account was already created for it, here. This section lists every account and
 * lets a super admin add one (a coachcedar.com Google address + a role), change a role,
 * and deactivate or reactivate an account. The last active super admin can be neither
 * deactivated nor demoted (the server refuses with 409; the page does not offer it).
 *
 * Endpoints (routers/admin_auth.py, all super_admin only, all audited):
 *   GET   /api/admin/auth/me              — is the signed-in admin a super admin?
 *   GET   /api/admin/auth/admins
 *   POST  /api/admin/auth/create-admin    — { email, role } (no password: Google only)
 *   PATCH /api/admin/auth/admins/:id      — { role } | { active }
 *
 * Renders nothing for any other role.
 */

import React, { useMemo, useState } from 'react';
import { KeyRound, Plus, ShieldAlert } from 'lucide-react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { AdminApiError, adminFetch } from '../../../utils/adminApi';
import {
  ADMIN_GOOGLE_DOMAIN,
  ADMIN_ROLES,
  AdminAccount,
  AccountChange,
  ROLE_LABELS,
  accountChangeRefusal,
  isAdminRole,
  newAdminEmailError,
  normalizeAdminEmail,
  sortAccounts,
} from './adminAccountsLogic';

const ACCOUNTS_KEY = ['admin-accounts'];

/** The server's own message for a refused request (FastAPI's `detail`), else a fallback. */
function errorText(err: unknown): string {
  if (err instanceof AdminApiError) {
    try {
      const body = JSON.parse(err.body);
      if (typeof body?.detail === 'string') return body.detail;
      if (Array.isArray(body?.detail) && typeof body.detail[0]?.msg === 'string') {
        return body.detail[0].msg;
      }
    } catch {
      // not JSON
    }
    return `Request failed (HTTP ${err.status})`;
  }
  return err instanceof Error ? err.message : 'Request failed';
}

export function AdminAccountsSection() {
  const me = useQuery<{ admin?: { role?: string } }>({
    queryKey: ['admin-me'],
    queryFn: () => adminFetch('/api/admin/auth/me'),
  });
  const isSuperAdmin = me.data?.admin?.role === 'super_admin';
  if (!isSuperAdmin) return null;
  return <AdminAccountsPanel />;
}

function AdminAccountsPanel() {
  const qc = useQueryClient();
  const [email, setEmail] = useState('');
  const [role, setRole] = useState<string>('admin');
  const [formError, setFormError] = useState<string | null>(null);
  const [rowError, setRowError] = useState<string | null>(null);

  const { data, isLoading, error } = useQuery<{ admins: AdminAccount[] }>({
    queryKey: ACCOUNTS_KEY,
    queryFn: () => adminFetch('/api/admin/auth/admins'),
  });
  const accounts = useMemo(() => sortAccounts(data?.admins ?? []), [data]);

  const refresh = () => {
    qc.invalidateQueries({ queryKey: ACCOUNTS_KEY });
    qc.invalidateQueries({ queryKey: ['admin-team'] });
  };

  const create = useMutation({
    mutationFn: (body: { email: string; role: string }) =>
      adminFetch('/api/admin/auth/create-admin', { method: 'POST', body: JSON.stringify(body) }),
    onSuccess: () => {
      setEmail('');
      setFormError(null);
      refresh();
    },
    onError: (err: unknown) => setFormError(errorText(err)),
  });

  const update = useMutation({
    mutationFn: ({ id, change }: { id: string; change: AccountChange }) =>
      adminFetch(`/api/admin/auth/admins/${encodeURIComponent(id)}`, {
        method: 'PATCH',
        body: JSON.stringify(change),
      }),
    onSuccess: () => {
      setRowError(null);
      refresh();
    },
    onError: (err: unknown) => setRowError(errorText(err)),
  });

  const submit = (e: React.FormEvent) => {
    e.preventDefault();
    const problem = newAdminEmailError(email) ?? (isAdminRole(role) ? null : 'Pick a role.');
    if (problem) {
      setFormError(problem);
      return;
    }
    create.mutate({ email: normalizeAdminEmail(email), role });
  };

  const change = (account: AdminAccount, next: AccountChange) => {
    const refusal = accountChangeRefusal(account, next, accounts);
    if (refusal) {
      setRowError(refusal);
      return;
    }
    update.mutate({ id: account.admin_id, change: next });
  };

  return (
    <section
      className="space-y-4 rounded-lg bg-white p-5"
      style={{ boxShadow: '0px 1px 3px rgba(0,0,0,0.06)' }}
      data-testid="admin-accounts-section"
    >
      <div>
        <h2 className="flex items-center gap-2 text-sm font-semibold text-gray-900">
          <KeyRound className="h-4 w-4" style={{ color: '#1F6C4F' }} />
          Admin accounts
        </h2>
        <p className="mt-1 text-xs text-gray-500">
          The Admin Portal is Google sign-in only. A {ADMIN_GOOGLE_DOMAIN} Google account can
          sign in only if it has an active account here. Super admins only.
        </p>
      </div>

      <form onSubmit={submit} className="flex flex-wrap items-end gap-2">
        <label className="flex flex-col text-[11px] font-medium text-gray-600">
          Google email
          <input
            type="email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            placeholder={`name@${ADMIN_GOOGLE_DOMAIN}`}
            className="mt-1 w-72 rounded-md border border-gray-300 px-3 py-1.5 text-xs"
            disabled={create.isPending}
          />
        </label>
        <label className="flex flex-col text-[11px] font-medium text-gray-600">
          Role
          <select
            value={role}
            onChange={(e) => setRole(e.target.value)}
            className="mt-1 rounded-md border border-gray-300 px-2 py-1.5 text-xs"
            disabled={create.isPending}
          >
            {ADMIN_ROLES.map((r) => (
              <option key={r} value={r}>{ROLE_LABELS[r]}</option>
            ))}
          </select>
        </label>
        <button
          type="submit"
          disabled={create.isPending}
          className="inline-flex items-center gap-1.5 rounded-md px-3 py-1.5 text-xs font-medium text-white disabled:opacity-50"
          style={{ backgroundColor: '#1F6C4F' }}
        >
          <Plus className="h-3.5 w-3.5" />
          {create.isPending ? 'Adding…' : 'Add account'}
        </button>
      </form>
      {formError && <p className="text-xs text-red-600" role="alert">{formError}</p>}

      {rowError && (
        <p className="flex items-center gap-1.5 text-xs text-red-600" role="alert">
          <ShieldAlert className="h-3.5 w-3.5" />
          {rowError}
        </p>
      )}

      {isLoading && <div className="py-6 text-center text-xs text-gray-400">Loading…</div>}
      {error && <div className="py-6 text-center text-xs text-red-600">{errorText(error)}</div>}

      {!isLoading && !error && (
        <table className="w-full text-xs">
          <thead className="border-b border-gray-100">
            <tr className="text-[10px] font-semibold uppercase tracking-wide text-gray-500">
              <th className="px-3 py-2 text-left">Account</th>
              <th className="px-3 py-2 text-left">Role</th>
              <th className="px-3 py-2 text-left">Status</th>
              <th className="px-3 py-2 text-left">Last sign-in</th>
              <th className="px-3 py-2 text-right">Access</th>
            </tr>
          </thead>
          <tbody>
            {accounts.map((a) => {
              const deactivateRefusal = accountChangeRefusal(a, { active: false }, accounts);
              return (
                <tr key={a.admin_id} className="border-b border-gray-50">
                  <td className="px-3 py-2">
                    <div className="font-medium text-gray-900">{a.email}</div>
                    {a.full_name && <div className="text-[11px] text-gray-500">{a.full_name}</div>}
                  </td>
                  <td className="px-3 py-2">
                    <select
                      aria-label={`Role for ${a.email}`}
                      value={a.role}
                      disabled={update.isPending}
                      onChange={(e) => change(a, { role: e.target.value })}
                      className="rounded-md border border-gray-300 px-2 py-1 text-xs"
                    >
                      {ADMIN_ROLES.map((r) => (
                        <option
                          key={r}
                          value={r}
                          disabled={r !== a.role && accountChangeRefusal(a, { role: r }, accounts) !== null}
                        >
                          {ROLE_LABELS[r]}
                        </option>
                      ))}
                    </select>
                  </td>
                  <td className="px-3 py-2">
                    {a.active
                      ? <span className="font-medium" style={{ color: '#1F6C4F' }}>Active</span>
                      : <span className="text-gray-400">Deactivated</span>}
                  </td>
                  <td className="px-3 py-2 text-gray-500">
                    {a.last_login_at ? new Date(a.last_login_at).toLocaleString() : '—'}
                  </td>
                  <td className="px-3 py-2 text-right">
                    {a.active ? (
                      <button
                        type="button"
                        disabled={update.isPending || deactivateRefusal !== null}
                        title={deactivateRefusal ?? undefined}
                        onClick={() => change(a, { active: false })}
                        className="text-[11px] font-medium text-red-600 hover:underline disabled:cursor-not-allowed disabled:text-gray-300 disabled:no-underline"
                      >
                        Deactivate
                      </button>
                    ) : (
                      <button
                        type="button"
                        disabled={update.isPending}
                        onClick={() => change(a, { active: true })}
                        className="text-[11px] font-medium hover:underline"
                        style={{ color: '#1F6C4F' }}
                      >
                        Reactivate
                      </button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      )}
    </section>
  );
}

export default AdminAccountsSection;
